import { useQuery, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export interface MetricsSummary {
  totalUsers: number;
  totalTeams: number;
  activeSubscriptions: number;
  manualGrants: number;
  totalStorageBytes: number;
  pseudoMrrCents: number;
}

export interface TimeseriesBucket {
  date: string;
  signups: number;
  newSubscriptions: number;
  cancellations: number;
}

export interface PlanDistribution {
  planCode: string;
  teamCount: number;
}

export interface StorageTopTeam {
  teamId: string;
  teamSlug: string;
  teamName: string;
  storageUsedBytes: number;
  planCode: string;
}

const SUMMARY_KEY: QueryKey = ['admin', 'metrics', 'summary'];

export function useMetricsSummary() {
  return useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: () => api<MetricsSummary>('/api/admin/metrics/summary'),
    staleTime: 60_000,
  });
}

export function useMetricsTimeseries(days = 30) {
  return useQuery({
    queryKey: ['admin', 'metrics', 'timeseries', days],
    queryFn: () => api<TimeseriesBucket[]>(`/api/admin/metrics/timeseries?days=${days}`),
    staleTime: 60_000,
  });
}

export function useMetricsPlanDistribution() {
  return useQuery({
    queryKey: ['admin', 'metrics', 'plans-distribution'],
    queryFn: () => api<PlanDistribution[]>('/api/admin/metrics/plans-distribution'),
    staleTime: 60_000,
  });
}

export function useMetricsTopStorage(limit = 20) {
  return useQuery({
    queryKey: ['admin', 'metrics', 'storage', limit],
    queryFn: () => api<StorageTopTeam[]>(`/api/admin/metrics/storage?limit=${limit}`),
    staleTime: 60_000,
  });
}

// ── Subscription health widgets ──────────────────────────────────────────

export type SubscriptionStatus =
  | 'free'
  | 'trial'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'lifetime';

export interface SubscriptionBreakdown {
  byStatus: Record<SubscriptionStatus, number>;
  mrrByPlan: Array<{ planCode: string; cents: number; subscriptions: number }>;
  trialsExpiringSoon: number;
  pastDueCount: number;
}

export interface SubscriptionNeedingAttention {
  id: string;
  customerType: 'user' | 'team';
  customerId: string;
  customerLabel: string;
  planCode: string;
  status: SubscriptionStatus;
  attentionAt: string;
  reason: 'trial_expiring' | 'past_due';
}

export function useMetricsSubscriptionBreakdown() {
  return useQuery({
    queryKey: ['admin', 'metrics', 'subscription-breakdown'],
    queryFn: () => api<SubscriptionBreakdown>('/api/admin/metrics/subscription-breakdown'),
    staleTime: 60_000,
  });
}

export function useMetricsSubscriptionsNeedingAttention() {
  return useQuery({
    queryKey: ['admin', 'metrics', 'subscriptions-needing-attention'],
    queryFn: () =>
      api<SubscriptionNeedingAttention[]>('/api/admin/metrics/subscriptions-needing-attention'),
    staleTime: 30_000, // shorter — these are actionable
  });
}

// ── AI usage / cost (admin-only — dollars OK here) ───────────────────────

export interface AiUsageTeam {
  teamId: string;
  teamName: string | null;
  costUsd: number;
  actions: number;
}

export interface AiUsageSummary {
  monthCostUsd: number;
  monthActions: number;
  topTeams: AiUsageTeam[];
}

export function useMetricsAiUsage() {
  return useQuery({
    queryKey: ['admin', 'metrics', 'ai-usage'],
    queryFn: () => api<AiUsageSummary>('/api/admin/metrics/ai-usage'),
    staleTime: 60_000,
  });
}
